import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader, EmptyState } from "@/components/common";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { UtensilsCrossed, Plus, Loader2, Leaf, Drumstick } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "Starters", cuisine: "North Indian", selling_price: 0, veg: true };
const COURSES = ["Starters", "Main Course", "Breads", "Rice", "Chinese", "South Indian", "Beverages", "Desserts"];

export default function Menu() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products").then((r) => setItems(r.data.filter((p) => p.industry === "restaurant" || true)));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/products", { ...form, selling_price: +form.selling_price, purchase_price: Math.round(+form.selling_price * 0.38), quantity: 999, industry: "restaurant", low_stock_threshold: 0 });
      toast.success("Dish added to menu");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  if (!items) return <Loader />;
  const grouped = COURSES.map((c) => [c, items.filter((i) => i.category === c)]).filter(([, arr]) => arr.length);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="menu-page">
      <PageHeader title="Menu" subtitle={`${items.length} dishes across your kitchen`}>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-dish-btn" className="font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Dish</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Add Dish</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Dish name</Label><Input data-testid="dish-name" value={form.name} onChange={set("name")} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Course</Label>
                  <select value={form.category} onChange={set("category")} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    {COURSES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><Label>Price ₹</Label><Input type="number" data-testid="dish-price" value={form.selling_price} onChange={set("selling_price")} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Cuisine</Label><Input value={form.cuisine} onChange={set("cuisine")} className="mt-1" /></div>
                <div><Label>Type</Label>
                  <select value={form.veg ? "veg" : "non"} onChange={(e) => setForm((f) => ({ ...f, veg: e.target.value === "veg" }))} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="veg">Veg</option><option value="non">Non-Veg</option>
                  </select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving || !form.name} data-testid="save-dish-btn" className="font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Dish"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {grouped.length === 0 ? <EmptyState title="No dishes yet" description="Add your first dish to build the menu." icon={UtensilsCrossed} /> :
        grouped.map(([course, arr]) => (
          <SectionCard key={course} title={course}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {arr.map((d) => (
                <div key={d.id} data-testid={`dish-${d.id}`} className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 p-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`grid place-items-center h-5 w-5 rounded border ${d.veg ? "border-[hsl(var(--success))]" : "border-destructive"}`}>
                      {d.veg ? <Leaf className="h-3 w-3 text-[hsl(var(--success))]" /> : <Drumstick className="h-3 w-3 text-destructive" />}
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{d.name}</div>
                      <div className="text-xs text-muted-foreground">{d.cuisine || d.category}</div>
                    </div>
                  </div>
                  <div className="font-semibold tabular-nums">{formatINR(d.selling_price)}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        ))}
    </div>
  );
}
